import { FC, useCallback, useEffect } from 'react';
import { Controller, ResolverOptions, useForm } from 'react-hook-form';
import { useFetcher, useLocation } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { z } from 'zod';
import { N_STAGE_OPTIONS } from '@samd/features/orders';
import { PatientIdField } from '@samd/features/orders/components/patient_id_form_field';
import { IntegerInput } from 'src/components/integer_input';
import { RadioGroupField } from 'src/components/radio_group';
import {
  emptyToUndefined,
  FormActions,
  FormFieldProps,
  processFetcherResponse,
  safeEmptyToUndefined,
  submitOrderAction,
} from 'src/distribution_platforms/features/order_form';
import {
  SamdOrderDetailFieldsFragment,
  OrderStatus,
  SpecimenType,
  NStage,
} from 'src/types';
import { OrderAction } from 'src/types/loaders';
import { notifyError, notifySuccess, SpecimenTypeUtils } from 'src/utils';

const FORM_CONSTANTS = {
  WIDTH: '600px',
  MIN_AGE: 18,
  MAX_AGE: 100,
  MIN_TUMOR_SIZE: 0,
  MAX_TUMOR_SIZE: 150,
} as const;

export type BreastOrderFormData = z.input<typeof BreastOrderFormSchema>;

export const BreastOrderFormSchema = z.object({
  externalId: z.string().min(1, 'Patient ID is required'),
  patientAge: z
    .number({
      error: (issue) => {
        if (issue.code === 'invalid_type') {
          return { message: 'Patient age is required' };
        }

        return null;
      },
    })
    .min(
      FORM_CONSTANTS.MIN_AGE,
      `Acceptable age range for patient is from ${FORM_CONSTANTS.MIN_AGE} to ${FORM_CONSTANTS.MAX_AGE}`,
    )
    .max(
      FORM_CONSTANTS.MAX_AGE,
      `Acceptable age range for patient is from ${FORM_CONSTANTS.MIN_AGE} to ${FORM_CONSTANTS.MAX_AGE}`,
    )
    .nullable()
    .superRefine((val, ctx) => {
      if (val === null) {
        ctx.addIssue({ code: 'custom', message: 'Patient age is required' });
      }
    }),
  tumorSize: z
    .number({
      error: (issue) => {
        if (issue.code === 'invalid_type') {
          return { message: 'Tumor size is required' };
        }

        return null;
      },
    })
    .min(
      FORM_CONSTANTS.MIN_TUMOR_SIZE,
      `Acceptable tumor size for patient is from ${FORM_CONSTANTS.MIN_TUMOR_SIZE} to ${FORM_CONSTANTS.MAX_TUMOR_SIZE}`,
    )
    .int('Tumor size must be a whole number')
    .max(
      FORM_CONSTANTS.MAX_TUMOR_SIZE,
      `Acceptable tumor size for patient is from ${FORM_CONSTANTS.MIN_TUMOR_SIZE} to ${FORM_CONSTANTS.MAX_TUMOR_SIZE}`,
    )
    .nullable()
    .superRefine((val, ctx) => {
      if (val === null) {
        ctx.addIssue({ code: 'custom', message: 'Tumor size is required' });
      }
    }),
  nStage: z
    .string()
    .min(1, 'Pathological Nodal Stage is required')
    .refine(
      (val) => Object.values(NStage).includes(val as NStage),
      'Invalid Pathological Nodal Stage selection',
    ),
});

// Custom resolver
const createCustomResolver =
  () =>
  async (
    data: BreastOrderFormData,
    context: unknown,
    options: ResolverOptions<BreastOrderFormData>,
  ) => {
    return zodResolver(BreastOrderFormSchema)(data, context, options);
  };

// Helper function to create default form values
const createDefaultFormValues = (
  initialOrder: SamdOrderDetailFieldsFragment,
): BreastOrderFormData => ({
  externalId: initialOrder.externalId?.toString() ?? '',
  patientAge: initialOrder.patientAge ?? null,
  tumorSize: initialOrder.tumorSize ?? null,
  nStage: initialOrder.nStage ?? '',
});

const PatientAgeField: FC<FormFieldProps<BreastOrderFormData>> = ({
  control,
  errors,
  trigger,
}) => {
  return (
    <Controller
      name='patientAge'
      control={control}
      render={({ field }) => (
        <IntegerInput
          required
          label='Age at Diagnosis'
          data-testid='patientAge'
          {...field}
          error={!!errors.patientAge}
          helperText={errors.patientAge?.message?.toString() ?? ' '}
          min={FORM_CONSTANTS.MIN_AGE}
          max={FORM_CONSTANTS.MAX_AGE}
          onChange={(value) => {
            field.onChange(value);
            trigger?.('patientAge');
          }}
        />
      )}
    />
  );
};

const TumorSizeField: FC<FormFieldProps<BreastOrderFormData>> = ({
  control,
  errors,
  trigger,
}) => {
  return (
    <Controller
      name='tumorSize'
      control={control}
      render={({ field }) => (
        <IntegerInput
          required
          label='Tumor Size (mm)'
          data-testid='tumorSize'
          {...field}
          error={!!errors.tumorSize}
          helperText={errors.tumorSize?.message?.toString() ?? ' '}
          min={FORM_CONSTANTS.MIN_TUMOR_SIZE}
          max={FORM_CONSTANTS.MAX_TUMOR_SIZE}
          onChange={(value) => {
            field.onChange(value);
            trigger?.('tumorSize');
          }}
        />
      )}
    />
  );
};

const NStageField: FC<FormFieldProps<BreastOrderFormData>> = ({
  control,
  errors,
  trigger,
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      gap: { xs: 2, md: 3 },
    }}
  >
    <Controller
      name='nStage'
      control={control}
      render={({ field }) => (
        <RadioGroupField
          name={field.name}
          label='Pathological Nodal Stage'
          value={field.value}
          onBlur={field.onBlur}
          options={N_STAGE_OPTIONS}
          required
          row
          validationText={errors.nStage?.message?.toString() ?? ''}
          onChange={(e) => {
            field.onChange(e);
            trigger?.('nStage');
          }}
        />
      )}
    />
  </Box>
);

export const SamdBreastOrderForm: FC<{
  initialOrder: SamdOrderDetailFieldsFragment;
  onClose: () => void;
}> = ({ initialOrder, onClose }) => {
  const theme = useTheme();
  const fetcher = useFetcher();
  const location = useLocation();
  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
    getValues,
  } = useForm<BreastOrderFormData>({
    resolver: createCustomResolver(),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: createDefaultFormValues(initialOrder),
  });

  // Convert form data to Order object
  const getOrderFromFormData =
    useCallback((): SamdOrderDetailFieldsFragment => {
      const formData = getValues();

      return {
        ...initialOrder,
        externalId: emptyToUndefined(formData.externalId),
        patientAge: formData.patientAge,
        tumorSize: formData.tumorSize,
        nStage: safeEmptyToUndefined(formData.nStage) as NStage | undefined,
        specimenType: SpecimenType.BreastBiopsy,
        status: initialOrder.status,
      } as SamdOrderDetailFieldsFragment;
    }, [getValues, initialOrder]);

  // Handle action results
  useEffect(() => {
    const result = processFetcherResponse(fetcher.data, fetcher.state);

    if (result) {
      if (result.type === 'success') {
        notifySuccess(result.message);
        if (result.closeDialog) {
          onClose();
        }
      } else if (result.type === 'error') {
        notifyError(result.message);
      }
    }
  }, [fetcher.data, fetcher.state, onClose]);

  // Trigger validation for edit forms to show validation errors immediately
  useEffect(() => {
    if (initialOrder.id) {
      trigger();
    }
  }, [initialOrder, trigger]);

  const isNewOrder = !initialOrder.id;
  const isDraftOrder = initialOrder.status === OrderStatus.Draft;

  const handleSaveOrder = async () => {
    const orderData = getOrderFromFormData();

    if (!orderData.id) {
      submitOrderAction(
        fetcher.submit,
        OrderAction.CREATE,
        location,
        orderData,
      );
    } else {
      submitOrderAction(
        fetcher.submit,
        OrderAction.UPDATE,
        location,
        orderData,
      );
    }
  };

  const handleSubmitOrder = async () => {
    const orderData = getOrderFromFormData();

    // Decide between submit and resubmit based on order status
    // Use RESUBMIT for orders that have been submitted before (not Draft status)
    const action =
      orderData.status === OrderStatus.Draft
        ? OrderAction.SUBMIT
        : OrderAction.RESUBMIT;

    submitOrderAction(fetcher.submit, action, location, orderData);
  };

  const handleDiscard = async () => {
    // Unsaved orders do not need to be deleted because they don't exist yet
    if (isDraftOrder && !isNewOrder) {
      submitOrderAction(
        fetcher.submit,
        OrderAction.DELETE,
        location,
        undefined,
      );
    } else {
      notifySuccess('Order discarded successfully.');
      onClose();
    }
  };

  const formTitle = `${isNewOrder ? 'Create' : 'Edit'} ${SpecimenTypeUtils.getSpecimenTypeName(SpecimenType.BreastBiopsy)} Test Order`;

  return (
    <Box
      component='form'
      onSubmit={handleSubmit(handleSubmitOrder)}
      noValidate
      sx={{
        width: '100%',
        maxWidth: FORM_CONSTANTS.WIDTH,
        px: { xs: 0, sm: 2, md: 0 },
      }}
    >
      <Typography
        variant='h5'
        sx={{
          display: 'block',
          fontWeight: theme.typography.fontWeightBold,

          fontSize: {
            xs: theme.typography.h6.fontSize,
            sm: theme.typography.h5.fontSize,
          },
        }}
      >
        {formTitle}
      </Typography>
      <Typography
        variant='caption'
        sx={{
          display: 'block',
          color: 'text.secondary',
          mb: 2,
        }}
      >
        *Indicates a required field
      </Typography>
      <Typography
        variant='caption'
        sx={{
          display: 'block',
          color: 'text.secondary',
          mb: 1,
        }}
      >
        Protected Health Information, such as patient name, date of birth, or
        medical record number, should not be entered into this web portal.
      </Typography>
      <Stack spacing={{ xs: 2, sm: 2 }}>
        <PatientIdField
          control={control}
          errors={errors}
          onChange={async (_) => {
            await trigger('externalId');
          }}
        />
        <PatientAgeField control={control} errors={errors} trigger={trigger} />
        <TumorSizeField control={control} errors={errors} trigger={trigger} />
        <NStageField control={control} errors={errors} trigger={trigger} />
        <FormActions
          showSubmitButton={true}
          showSaveButton={false}
          showDraftActionButtons={isDraftOrder || isNewOrder}
          onValidateBeforeSubmit={async () => trigger()}
          onConfirmSubmit={handleSubmitOrder}
          onSaveAsDraft={handleSaveOrder}
          onDiscard={handleDiscard}
          requireDiscardConfirmation={!isNewOrder}
          onDiscardSuccess={() => {
            notifySuccess('Order discarded successfully.');
            onClose();
          }}
        />
      </Stack>
    </Box>
  );
};
